import { Layout } from 'antd';
import type React from 'react';
import { useLocation } from 'react-router-dom';
import { IconAnalytics } from 'src/components/icons/icons-sidebar/icon-analytics';
import { IconChurnRate } from 'src/components/icons/icons-sidebar/icon-churn-rate';
import { IconCostOptimization } from 'src/components/icons/icons-sidebar/icon-cost-optimization';
import { IconGameCollection } from 'src/components/icons/icons-sidebar/icon-game-collection';
import { IconGamePromotion } from 'src/components/icons/icons-sidebar/icon-game-promotion';
import { IconIntegration } from 'src/components/icons/icons-sidebar/icon-integration';
import { IconIntegrationGuide } from 'src/components/icons/icons-sidebar/icon-integration-guide';
import { IconIntegrationStatus } from 'src/components/icons/icons-sidebar/icon-integration-status';
import { IconOverview } from 'src/components/icons/icons-sidebar/icon-overview';
import { IconPlatforms } from 'src/components/icons/icons-sidebar/icon-platforms';
import { IconPriorityImpact } from 'src/components/icons/icons-sidebar/icon-priority-impact';
import { IconRecommendations } from 'src/components/icons/icons-sidebar/icon-recommendations';
import { EntitlementsAccess, GettingStartedTab, IntegrationTab, Route } from 'src/constants';
import { useIsPlatformIntegrated } from 'src/hooks/data/platforms';
import { useUser } from 'src/hooks/data/user';
import { SidebarLink } from './sidebar-link/sidebar-link';
import { SidebarTitle } from './sidebar-title/sidebar-title';

const getRoute = (pathname: string) => pathname.match(/^\/[^/]*/)?.[0];

const hasAccess = (user: ReturnType<typeof useUser>, entitlement: string) =>
  !!user?.isAdmin || !!user?.entitlements.includes(entitlement);

export const Sidebar: React.FC = () => {
  const { pathname } = useLocation();
  const user = useUser();
  const { isIntegrated, isInProgress } = useIsPlatformIntegrated();
  const isAdminRoute = !!user?.isAdmin;

  return (
    <Layout.Sider width={216} className="px-12 py-16">
      {!!user && !isInProgress && (
        <>
          {hasAccess(user, EntitlementsAccess.OVERVIEW) && (
            <SidebarLink
              id="overview"
              labelId={user.isAdmin || isIntegrated ? undefined : 'getting-started'}
              icon={<IconOverview />}
              to={Route.OVERVIEW}
              isActive={pathname === Route.OVERVIEW || getRoute(pathname) === Route.GETTING_STARTED}
            />
          )}

          {(hasAccess(user, EntitlementsAccess.ANALYTICS) ||
            hasAccess(user, EntitlementsAccess.CHURN) ||
            hasAccess(user, EntitlementsAccess.PRIORITY_IMPACT)) && <SidebarTitle id="analysis" />}

          {hasAccess(user, EntitlementsAccess.PRIORITY_IMPACT) && (
            <SidebarLink
              id="priority-impact"
              icon={<IconPriorityImpact />}
              to={Route.PRIORITY_IMPACT}
              isActive={getRoute(pathname) === Route.PRIORITY_IMPACT}
            />
          )}

          {hasAccess(user, EntitlementsAccess.ANALYTICS) && (
            <SidebarLink
              id="analytics"
              icon={<IconAnalytics />}
              to={Route.ANALYTICS}
              isActive={getRoute(pathname) === Route.ANALYTICS}
            />
          )}

          {hasAccess(user, EntitlementsAccess.CHURN) && (
            <SidebarLink
              id="churn-rate"
              icon={<IconChurnRate />}
              to={Route.CHURN_RATE}
              isActive={getRoute(pathname) === Route.CHURN_RATE}
            />
          )}

          {(hasAccess(user, EntitlementsAccess.INTEGRATION) ||
            hasAccess(user, EntitlementsAccess.GAME_ADJUSTMENTS) ||
            hasAccess(user, EntitlementsAccess.GAME_RECS) ||
            hasAccess(user, EntitlementsAccess.COST_OPTIMIZATION)) && <SidebarTitle id="administration" />}

          {isAdminRoute && (
            <SidebarLink
              id="integration-status"
              icon={<IconIntegrationStatus />}
              to={Route.INTEGRATION_STATUS}
              isActive={getRoute(pathname) === Route.INTEGRATION_STATUS}
            />
          )}

          {isAdminRoute && (
            <SidebarLink
              id="platforms"
              icon={<IconPlatforms />}
              to={Route.PLATFORMS}
              isActive={getRoute(pathname) === Route.PLATFORMS}
            />
          )}

          {!isAdminRoute && hasAccess(user, EntitlementsAccess.INTEGRATION) && (
            <SidebarLink
              id="integration"
              icon={<IconIntegration />}
              to={`${Route.INTEGRATION}/${IntegrationTab.DATA}`}
              isActive={getRoute(pathname) === Route.INTEGRATION}
            />
          )}

          {hasAccess(user, EntitlementsAccess.GAME_ADJUSTMENTS) && (
            <SidebarLink
              id="game-promotion"
              icon={<IconGamePromotion />}
              to={Route.GAME_PROMOTION}
              isActive={getRoute(pathname) === Route.GAME_PROMOTION}
            />
          )}

          {hasAccess(user, EntitlementsAccess.COST_OPTIMIZATION) && (
            <SidebarLink
              id="cost-optimization"
              icon={<IconCostOptimization />}
              to={Route.COST_OPTIMIZATION}
              isActive={getRoute(pathname) === Route.COST_OPTIMIZATION}
            />
          )}

          {hasAccess(user, EntitlementsAccess.GAME_COLLECTION) && (
            <SidebarLink
              id="game-collection"
              icon={<IconGameCollection />}
              to={Route.GAME_COLLECTION}
              isActive={getRoute(pathname) === Route.GAME_COLLECTION}
            />
          )}

          {hasAccess(user, EntitlementsAccess.GAME_RECS) && (
            <SidebarLink
              id="recommendations"
              icon={<IconRecommendations />}
              to={Route.RECOMMENDATIONS}
              isActive={getRoute(pathname) === Route.RECOMMENDATIONS}
            />
          )}

          {hasAccess(user, EntitlementsAccess.INTEGRATION_GUIDE) && (
            <>
              <SidebarTitle id="documentation" />
              <SidebarLink
                id="integration-guide"
                icon={<IconIntegrationGuide />}
                to={`${Route.INTEGRATION_GUIDE}/${GettingStartedTab.DATA}`}
                isActive={getRoute(pathname) === Route.INTEGRATION_GUIDE}
              />
            </>
          )}
        </>
      )}
    </Layout.Sider>
  );
};
